import { useEffect, useState } from "react";

const API = process.env.REACT_APP_API_URL || "http://localhost:5001";
const token = () => localStorage.getItem("adminToken");

const PAGE_LABELS = {
  home:    "🏠 Home Page",
  shop:    "🛍️ Shop Page",
  jewelry: "💍 Jewelry Page",
  contact: "📞 Contact Page",
};

function CharCount({ value, max }) {
  const len = (value || "").length;
  const color = len > max ? "text-red-500" : len > max * 0.8 ? "text-yellow-500" : "text-green-500";
  return <span className={`text-xs font-medium ${color}`}>{len}/{max}</span>;
}

// ── Page SEO Editor ───────────────────────────────────────────
function PageSEOCard({ page, data, onSave }) {
  const [form,    setForm]    = useState(data);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [open,    setOpen]    = useState(false);

  useEffect(() => { setForm(data); }, [data]);

  function set(key, val) { setForm(f => ({ ...f, [key]: val })); }

  async function handleSave() {
    setSaving(true);
    try {
      await fetch(`${API}/api/admin/seo/pages/${page}`, {
        method:  "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body:    JSON.stringify(form),
      });
      onSave(page, form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      alert("Failed to save.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition">
        <div className="flex items-center gap-3">
          <span className="text-lg">{PAGE_LABELS[page]?.split(" ")[0]}</span>
          <div>
            <p className="text-sm font-semibold text-gray-900 text-left">{PAGE_LABELS[page]?.split(" ").slice(1).join(" ")}</p>
            <p className="text-xs text-gray-400 text-left truncate max-w-xs">{form?.title || "No title set"}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {form?.title && <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">Configured</span>}
          <svg className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {open && (
        <div className="px-6 pb-6 space-y-5 border-t border-gray-100 pt-5">

          {/* SEO Title */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium text-gray-700">SEO Title</label>
              <CharCount value={form?.title} max={60} />
            </div>
            <input
              value={form?.title || ""}
              onChange={e => set("title", e.target.value)}
              placeholder="Page title for search engines (50-60 chars)"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400"
            />
            <p className="text-xs text-gray-400 mt-1">Appears as the clickable headline in Google search results.</p>
          </div>

          {/* SEO Description */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium text-gray-700">Meta Description</label>
              <CharCount value={form?.description} max={160} />
            </div>
            <textarea
              rows={3}
              value={form?.description || ""}
              onChange={e => set("description", e.target.value)}
              placeholder="Brief description of the page (150-160 chars)"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400 resize-none"
            />
            <p className="text-xs text-gray-400 mt-1">Shown below the title in search results.</p>
          </div>

          {/* Keywords */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Keywords</label>
            <input
              value={form?.keywords || ""}
              onChange={e => set("keywords", e.target.value)}
              placeholder="keyword1, keyword2, keyword3"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400"
            />
            <p className="text-xs text-gray-400 mt-1">Comma-separated keywords related to this page.</p>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100 pt-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">Open Graph (Social Sharing)</p>

            {/* OG Title */}
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium text-gray-700">OG Title</label>
                  <CharCount value={form?.og_title} max={60} />
                </div>
                <input
                  value={form?.og_title || ""}
                  onChange={e => set("og_title", e.target.value)}
                  placeholder="Title when shared on Facebook, WhatsApp etc."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400"
                />
              </div>

              {/* OG Description */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium text-gray-700">OG Description</label>
                  <CharCount value={form?.og_description} max={160} />
                </div>
                <textarea
                  rows={2}
                  value={form?.og_description || ""}
                  onChange={e => set("og_description", e.target.value)}
                  placeholder="Description when shared on social media"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400 resize-none"
                />
              </div>

              {/* OG Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">OG Image URL</label>
                <input
                  value={form?.og_image || ""}
                  onChange={e => set("og_image", e.target.value)}
                  placeholder="https://... (recommended: 1200x630px)"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400"
                />
                {form?.og_image && (
                  <img src={form.og_image} alt="OG Preview" className="mt-2 h-24 rounded-lg object-cover border border-gray-200" />
                )}
              </div>
            </div>
          </div>

          {/* Preview */}
          {(form?.title || form?.description) && (
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">Google Preview</p>
              <div className="space-y-1">
                <p className="text-xs text-green-700">sagarfashion.com</p>
                <p className="text-base text-blue-700 font-medium leading-snug hover:underline cursor-pointer">{form?.title || "Page Title"}</p>
                <p className="text-xs text-gray-600 leading-relaxed">{form?.description || "Page description will appear here..."}</p>
              </div>
            </div>
          )}

          {/* Save */}
          <button
            onClick={handleSave} disabled={saving}
            className="bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white px-6 py-2.5 rounded-lg text-sm font-semibold transition">
            {saving ? "Saving..." : saved ? "✓ Saved!" : "Save Changes"}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Product SEO Editor ────────────────────────────────────────
function ProductSEORow({ product, onSave }) {
  const [form,   setForm]   = useState({
    seo_title:       product.seo_title       || "",
    seo_description: product.seo_description || "",
    seo_keywords:    product.seo_keywords    || "",
  });
  const [open,   setOpen]   = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);

  function set(key, val) { setForm(f => ({ ...f, [key]: val })); }

  async function handleSave() {
    setSaving(true);
    try {
      await fetch(`${API}/api/admin/seo/products/${product.id}`, {
        method:  "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body:    JSON.stringify(form),
      });
      onSave(product.id, form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      alert("Failed to save.");
    } finally {
      setSaving(false);
    }
  }

  const hasData = form.seo_title || form.seo_description || form.seo_keywords;

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 transition bg-white">
        <div className="flex items-center gap-3">
          <div>
            <p className="text-sm font-medium text-gray-900 text-left">{product.name}</p>
            <p className="text-xs text-gray-400 text-left">{product.category}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasData
            ? <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">SEO Set</span>
            : <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">No SEO</span>
          }
          <svg className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5 pt-4 space-y-4 border-t border-gray-100 bg-white">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium text-gray-700">SEO Title</label>
              <CharCount value={form.seo_title} max={60} />
            </div>
            <input
              value={form.seo_title}
              onChange={e => set("seo_title", e.target.value)}
              placeholder={`${product.name} — Sagar Fashion`}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium text-gray-700">SEO Description</label>
              <CharCount value={form.seo_description} max={160} />
            </div>
            <textarea
              rows={2}
              value={form.seo_description}
              onChange={e => set("seo_description", e.target.value)}
              placeholder="Describe this product for search engines..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400 resize-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Keywords</label>
            <input
              value={form.seo_keywords}
              onChange={e => set("seo_keywords", e.target.value)}
              placeholder="keyword1, keyword2, keyword3"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400"
            />
          </div>

          {/* Google Preview */}
          {(form.seo_title || form.seo_description) && (
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">Google Preview</p>
              <p className="text-xs text-green-700">sagarfashion.com/product/{product.id}</p>
              <p className="text-base text-blue-700 font-medium leading-snug">{form.seo_title || product.name}</p>
              <p className="text-xs text-gray-600 leading-relaxed">{form.seo_description || "Product description..."}</p>
            </div>
          )}

          <button onClick={handleSave} disabled={saving}
            className="bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white px-5 py-2 rounded-lg text-sm font-semibold transition">
            {saving ? "Saving..." : saved ? "✓ Saved!" : "Save"}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main SEO Page ─────────────────────────────────────────────
export default function SEO() {
  const [pagesSEO,    setPagesSEO]    = useState({});
  const [productsSEO, setProductsSEO] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [tab,         setTab]         = useState("pages");
  const [search,      setSearch]      = useState("");

  useEffect(() => {
    async function load() {
      try {
        const [pagesRes, productsRes] = await Promise.all([
          fetch(`${API}/api/admin/seo/pages`,    { headers: { Authorization: `Bearer ${token()}` } }),
          fetch(`${API}/api/admin/seo/products`, { headers: { Authorization: `Bearer ${token()}` } }),
        ]);
        const pages    = await pagesRes.json();
        const products = await productsRes.json();

        // Convert pages array to object keyed by page
        const pagesMap = {};
        pages.forEach(p => { pagesMap[p.page] = p; });
        setPagesSEO(pagesMap);
        setProductsSEO(products);
      } catch {
        console.error("Failed to load SEO data.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  function onPageSave(page, data) {
    setPagesSEO(s => ({ ...s, [page]: { ...s[page], ...data } }));
  }

  function onProductSave(id, data) {
    setProductsSEO(ps => ps.map(p => p.id === id ? { ...p, ...data } : p));
  }

  const filteredProducts = productsSEO.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.category.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-gray-400 text-sm animate-pulse">Loading SEO settings...</div>
    </div>
  );

  return (
    <div className="p-6 max-w-4xl mx-auto">

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">SEO Management</h1>
        <p className="text-gray-500 text-sm mt-1">Manage meta tags, descriptions and keywords for all pages and products.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {[["pages", "📄 Pages", Object.keys(PAGE_LABELS).length], ["products", "📦 Products", productsSEO.length]].map(([key, label, count]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`px-5 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px ${
              tab === key
                ? "border-rose-500 text-rose-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}>
            {label} <span className="text-xs text-gray-400 ml-1">({count})</span>
          </button>
        ))}
      </div>

      {/* Pages Tab */}
      {tab === "pages" && (
        <div className="space-y-4">
          {Object.keys(PAGE_LABELS).map(page => (
            <PageSEOCard
              key={page}
              page={page}
              data={pagesSEO[page] || {}}
              onSave={onPageSave}
            />
          ))}
        </div>
      )}

      {/* Products Tab */}
      {tab === "products" && (
        <div className="space-y-3">
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search products..."
            className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-rose-400 mb-2"
          />
          {filteredProducts.map(p => (
            <ProductSEORow key={p.id} product={p} onSave={onProductSave} />
          ))}
        </div>
      )}
    </div>
  );
}